import React, { useState, useEffect, useRef } from 'react';
import { usersAPI, groupsAPI, messagesAPI } from '../services/api';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

export default function ChatDirect() {
  const { user }     = useAuth();
  const { on, emit } = useSocket();
  const [users, setUsers]   = useState([]);
  const [groups, setGroups] = useState([]);
  const [selGroup, setSelGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(false);
  const msgsEnd = useRef(null);

  useEffect(() => {
    usersAPI.list().then(r => setUsers(r.data.filter(u => u.id !== user?.id))).catch(()=>{});
  }, [user]);

  // Listen for new messages
  useEffect(() => {
    const unsub = on('new_message', (msg) => {
      if (selGroup && msg.group_id === selGroup.id) {
        setMessages(prev => [...prev, msg]);
      }
    });
    return unsub;
  }, [on, selGroup]);

  useEffect(() => {
    msgsEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openDM = async (contact) => {
    // find or create DM group
    setLoading(true);
    try {
      const all = await groupsAPI.list();
      const existing = all.data.find(g =>
        g.type === 'direct' &&
        g.member_count === 2
      );
      let group = existing;
      if (!group) {
        const r = await groupsAPI.create({
          name: `${user.full_name} & ${contact.full_name}`,
          type: 'direct',
          privacy: 'private',
          member_ids: [contact.id],
          avatar_color: contact.avatar_color,
          initials: contact.initials,
        });
        group = r.data;
      }
      setSelGroup({ ...group, contact });
      const msgs = await messagesAPI.get(group.id);
      setMessages(msgs.data);
    } catch { toast.error('فشل فتح المحادثة'); }
    finally { setLoading(false); }
  };

  const sendMsg = async () => {
    if (!text.trim() || !selGroup) return;
    const content = text; setText('');
    try {
      await messagesAPI.send({ group_id: selGroup.id, content, type: 'text' });
    } catch { toast.error('فشل الإرسال'); }
  };

  return (
    <div className="fade-in">
      <div className="toolbar">
        <button className="btn btn-primary">💬 رسالة جديدة</button>
        <button className="btn btn-outline" onClick={()=>window.print()}>🖨️ طباعة</button>
        <div className="search-bar"><input placeholder="🔍 بحث في المحادثات..." /></div>
      </div>

      <div className="chat-layout">
        {/* Contacts sidebar */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <div style={{fontSize:11.5,fontWeight:800,flex:1}}>المراسلات الفردية</div>
          </div>
          <div className="chat-list">
            {users.map(u => (
              <div key={u.id} className={`chat-item${selGroup?.contact?.id===u.id?' active':''}`} onClick={()=>openDM(u)}>
                <div className="avatar" style={{width:34,height:34,fontSize:11,background:u.avatar_color,color:'#fff'}}>{u.initials}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div className="chat-item-name">{u.full_name}</div>
                  <div className="chat-item-last">{u.job_title||u.role}</div>
                </div>
                <div style={{textAlign:'left'}}>
                  {u.is_online && <div className="online-indicator"/>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat main */}
        <div className="chat-main">
          {selGroup ? (<>
            <div className="chat-header">
              <div className="avatar" style={{width:34,height:34,fontSize:11,background:selGroup.contact?.avatar_color,color:'#fff'}}>{selGroup.contact?.initials}</div>
              <div>
                <div style={{fontSize:12,fontWeight:800}}>{selGroup.contact?.full_name}</div>
                <div style={{fontSize:9,color:selGroup.contact?.is_online?'var(--ok)':'var(--t3)'}}>
                  {selGroup.contact?.is_online?'● متصل الآن':'● غير متصل'}
                </div>
              </div>
              <div style={{marginRight:'auto',display:'flex',gap:5}}>
                <div className="icon-btn" title="مكالمة صوتية">📞</div>
                <div className="icon-btn" title="مكالمة مرئية">📹</div>
              </div>
            </div>

            <div className="messages-area">
              <div className="sys-msg"><span>{format(new Date(), 'EEEE d MMMM yyyy', {locale:ar})}</span></div>
              {loading && <div className="sys-msg"><span>⏳ جارٍ التحميل...</span></div>}
              {messages.map(m => (
                <div key={m.id} className={`message-row${m.sender_id===user?.id?' me':' them'}`}>
                  <div className="avatar" style={{width:26,height:26,fontSize:9,background:m.avatar_color||'#0B1E3D',color:'#fff'}}>{m.initials}</div>
                  <div>
                    <div className="message-bubble">{m.content}</div>
                    <div className="message-meta">{format(new Date(m.created_at),'HH:mm')}{m.sender_id===user?.id&&' ✓✓'}</div>
                  </div>
                </div>
              ))}
              <div ref={msgsEnd}/>
            </div>

            <div className="chat-input-bar">
              <div className="icon-btn" style={{width:30,height:30,fontSize:13}} title="إرسال ملف">📎</div>
              <textarea className="chat-textarea" rows={1} value={text} onChange={e=>setText(e.target.value)}
                placeholder="اكتب رسالة..."
                onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMsg();}}}
              />
              <button className="send-btn" onClick={sendMsg}>➤</button>
            </div>
          </>) : (
            <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12,color:'var(--t3)'}}>
              <span style={{fontSize:40}}>💬</span>
              <span style={{fontSize:12}}>اختر محادثة من القائمة</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
